import React, { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, Save, X } from 'lucide-react';
import {
  compactSelectClass,
  dangerBtnClass,
  fieldClass,
  labelClass,
  primaryBtnClass,
  secondaryBtnClass,
} from '../styles';
import {
  approveCreditOrder,
  cancelCreditOrder,
  fetchCreditOrders,
  updateCreditOrder,
} from './api';
import {
  CreditOrderFormValue,
  CreditOrderRow,
  OrdersMeta,
  formFromCreditOrder,
} from './types';

interface Props {
  meta: OrdersMeta;
}

const CreditOrdersPanel: React.FC<Props> = ({ meta }) => {
  const [rows, setRows] = useState<CreditOrderRow[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState<CreditOrderFormValue | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [orgFilter, setOrgFilter] = useState<number | ''>('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCreditOrders({
        status: statusFilter || undefined,
        org: orgFilter || undefined,
        q: search.trim() || undefined,
      });
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credit orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, orgFilter]);

  useEffect(() => {
    if (selectedId == null) {
      setForm(null);
      return;
    }
    const row = rows.find((r) => r.id === selectedId);
    if (row) setForm(formFromCreditOrder(row));
  }, [selectedId, rows]);

  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId]
  );
  const isDraft = selected?.status === 'Draft';

  const save = async () => {
    if (!selected || !form || !isDraft) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const updated = await updateCreditOrder(selected.id, form);
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setMessage('Credit order saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!selected || !isDraft) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      if (form) await updateCreditOrder(selected.id, form);
      const updated = await approveCreditOrder(selected.id);
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setMessage(
        updated.batch_expires_at
          ? `Approved. Batch expires ${new Date(updated.batch_expires_at).toLocaleString()}.`
          : 'Approved.'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!selected || !isDraft) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const updated = await cancelCreditOrder(selected.id);
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setMessage('Credit order cancelled.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {(error || message) && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-800'
          }`}
        >
          {error || message}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[520px]">
        <div className="lg:col-span-4 border border-[#E8E8E8] rounded-lg overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b border-[#E8E8E8] space-y-2">
            <div className="flex items-center gap-2">
              <input
                className={`${fieldClass} h-9`}
                placeholder="Search by email or name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void load();
                }}
              />
              <button type="button" className={secondaryBtnClass} onClick={() => void load()}>
                Search
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                className={compactSelectClass}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                {meta.credit_order_statuses.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <select
                className={compactSelectClass}
                value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value ? Number(e.target.value) : '')}
                aria-label="Filter by organization"
              >
                <option value="">All orgs</option>
                {meta.orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[#8A8A8A] p-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <ul className="flex-1 max-h-[480px] overflow-y-auto">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedId(row.id);
                      setMessage(null);
                      setError(null);
                    }}
                    className={`w-full px-3 py-2.5 text-left text-sm border-b border-[#F0F0F0] ${
                      selectedId === row.id
                        ? 'bg-[#F3F8FC] text-[#0C0B0B] font-medium'
                        : 'text-[#4A4A4A] hover:bg-[#FAFAFA]'
                    }`}
                  >
                    <div className="truncate">
                      {row.display_name || row.email}
                      {row.display_name ? ` · ${row.email}` : ''}
                    </div>
                    <div className="text-[11px] text-[#8A8A8A] mt-0.5">
                      {row.credits} cr · {row.currency_code} {row.total_cost} · {row.status}
                    </div>
                  </button>
                </li>
              ))}
              {rows.length === 0 && (
                <li className="px-3 py-6 text-sm text-[#8A8A8A]">No credit orders</li>
              )}
            </ul>
          )}
        </div>

        <div className="lg:col-span-8 border border-[#E8E8E8] rounded-lg p-4">
          {!selected || !form ? (
            <div className="h-full min-h-[280px] flex items-center justify-center text-sm text-[#8A8A8A] border border-dashed border-[#E0E0E0] rounded-lg">
              Select a credit order to review.
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                <div>
                  <h3 className="text-sm font-bold text-[#0C0B0B]">
                    Credit order #{selected.id}
                  </h3>
                  <p className="text-[12px] text-[#8A8A8A] mt-0.5">
                    Status: {selected.status}
                    {selected.created_at
                      ? ` · Created ${new Date(selected.created_at).toLocaleString()}`
                      : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isDraft && (
                    <>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => void save()}
                        className={`${secondaryBtnClass} inline-flex items-center gap-1.5`}
                      >
                        <Save className="w-3.5 h-3.5" />
                        Save
                      </button>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => void cancel()}
                        className={`${dangerBtnClass} inline-flex items-center gap-1.5`}
                      >
                        <X className="w-3.5 h-3.5" />
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => void approve()}
                        className={`${primaryBtnClass} inline-flex items-center gap-1.5`}
                      >
                        <Check className="w-3.5 h-3.5" />
                        Approve
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className={labelClass}>Requester</label>
                  <div className="text-sm text-[#0C0B0B]">
                    {selected.display_name || '—'}
                    <span className="text-[#8A8A8A]"> · {selected.email}</span>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Organization</label>
                  <select
                    className={fieldClass}
                    disabled={!isDraft}
                    value={form.org}
                    onChange={(e) =>
                      setForm({ ...form, org: e.target.value ? Number(e.target.value) : '' })
                    }
                  >
                    <option value="">Select org…</option>
                    {meta.orgs.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Currency</label>
                  <select
                    className={fieldClass}
                    disabled={!isDraft}
                    value={form.currency_code}
                    onChange={(e) => setForm({ ...form, currency_code: e.target.value })}
                  >
                    {meta.currencies.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Credits</label>
                  <input
                    type="number"
                    className={fieldClass}
                    disabled={!isDraft}
                    value={form.credits}
                    onChange={(e) => setForm({ ...form, credits: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>Cost per credit</label>
                  <input
                    className={fieldClass}
                    disabled={!isDraft}
                    value={form.cost_per_credit}
                    onChange={(e) => setForm({ ...form, cost_per_credit: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>Total cost</label>
                  <input
                    className={fieldClass}
                    disabled
                    value={(
                      (Number(form.credits) || 0) * (Number(form.cost_per_credit) || 0)
                    ).toFixed(2)}
                  />
                </div>
                {selected.approved_by_email && (
                  <div className="sm:col-span-2 text-[12px] text-[#8A8A8A]">
                    Approved by {selected.approved_by_email}
                    {selected.approved_at
                      ? ` on ${new Date(selected.approved_at).toLocaleString()}`
                      : ''}
                    {selected.batch_id ? ` · Batch #${selected.batch_id}` : ''}
                  </div>
                )}
                <div className="sm:col-span-2 text-[12px] text-[#8A8A8A]">
                  New batches expire after {meta.credit_expiry_months} months from approval.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditOrdersPanel;
